/**
 * Carte usage transcription Aircall (Whisper) :
 *  - Nombre de transcriptions ce mois / total
 *  - Coût Whisper estimé (€)
 *  - Toggle auto-transcription on webhook
 *
 * Endpoint : GET /api/aircall/transcription/usage + PUT /aircall/transcription/auto-toggle
 */
import { useEffect, useState, useMemo } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { Loader2, Mic, Euro, Zap, Calendar } from "lucide-react";
import { Switch } from "../ui/switch";

export default function AircallTranscriptionUsageCard() {
  const { axiosAuth } = useAuth();
  const api = useMemo(() => axiosAuth(), [axiosAuth]);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingLimit, setEditingLimit] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await api.get("/aircall/transcription/usage");
      setUsage(r.data);
    } catch {
      setUsage(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);

  const toggleAuto = async (enabled) => {
    setSaving(true);
    try {
      await api.put("/aircall/transcription/auto-toggle", { enabled });
      await reload();
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-[#0b0811] rounded-xl border border-white/5 p-4">
        <div className="flex items-center gap-2 text-xs text-slate-400">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Chargement de l'usage…
        </div>
      </div>
    );
  }

  if (!usage) return null;

  return (
    <div className="bg-[#0b0811] rounded-xl border border-violet-500/20 p-4 space-y-4" data-testid="whisper-usage-card">
      <div className="flex items-center gap-2">
        <div className="w-9 h-9 rounded-lg bg-violet-500/15 flex items-center justify-center">
          <Mic className="w-4 h-4 text-violet-400" />
        </div>
        <div>
          <h3 className="text-sm font-bold text-white">Transcription IA Aircall</h3>
          <p className="text-[11px] text-slate-500">Whisper-1 + détection de moments-clés Gemini · {usage.whisper_price_eur_per_min} €/min</p>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Kpi icon={<Calendar className="w-3.5 h-3.5" />} label="Ce mois" value={usage.this_month_transcriptions} unit="appels" />
        <Kpi icon={<Mic className="w-3.5 h-3.5" />} label="Total" value={usage.total_transcriptions} unit="appels" />
        <Kpi icon={<Euro className="w-3.5 h-3.5" />} label="Coût ce mois" value={Number(usage.this_month_cost_eur).toFixed(2)} unit="€" />
        <Kpi icon={<Euro className="w-3.5 h-3.5" />} label="Coût total" value={Number(usage.total_cost_eur).toFixed(2)} unit="€" />
      </div>

      {/* Quota du jour */}
      {usage.daily_limit != null && (
        <div className="bg-[#14101c] rounded-lg p-3 border border-amber-500/20" data-testid="daily-quota-row">
          <div className="flex items-center justify-between gap-2 mb-1.5">
            <div className="text-xs font-bold text-amber-300 inline-flex items-center gap-1.5">
              <Zap className="w-3 h-3" /> Quota Whisper aujourd'hui
            </div>
            <div className="text-xs text-slate-300 font-mono">
              <span className="text-white font-bold">{usage.today_used}</span>
              <span className="text-slate-500"> / {usage.daily_limit}</span>
            </div>
          </div>
          <div className="w-full h-1.5 bg-[#0b0811] rounded-full overflow-hidden">
            <div
              className={`h-full transition-all ${usage.today_used >= usage.daily_limit ? "bg-rose-500" : usage.today_used / usage.daily_limit > 0.8 ? "bg-amber-500" : "bg-emerald-500"}`}
              style={{ width: `${Math.min(100, 100 * (usage.today_used / Math.max(1, usage.daily_limit)))}%` }}
            />
          </div>
          <div className="flex items-center justify-between gap-2 mt-2">
            <span className="text-[10px] text-slate-500">Reste : <strong>{usage.today_remaining}</strong> auto-transcriptions aujourd'hui</span>
            <button onClick={() => setEditingLimit(true)} className="text-[10px] text-amber-300 hover:underline" data-testid="edit-daily-limit">
              Modifier la limite
            </button>
          </div>
          {editingLimit && (
            <div className="flex items-center gap-2 mt-2" data-testid="limit-edit-row">
              <input
                type="number"
                min="0"
                max="1000"
                defaultValue={usage.daily_limit}
                ref={(el) => { if (el) el.focus(); }}
                onKeyDown={async (e) => {
                  if (e.key === "Enter") {
                    const n = parseInt(e.target.value, 10);
                    if (!isNaN(n)) {
                      await api.put("/aircall/transcription/daily-limit", { limit: n });
                      setEditingLimit(false);
                      reload();
                    }
                  }
                  if (e.key === "Escape") setEditingLimit(false);
                }}
                className="bg-[#0b0811] border border-white/10 rounded text-white text-xs px-2 py-1 w-20"
                data-testid="limit-input"
              />
              <span className="text-[10px] text-slate-500">Entrée pour valider · Esc pour annuler</span>
            </div>
          )}
        </div>
      )}

      {/* Toggle auto-transcribe */}
      <div className="bg-[#14101c] rounded-lg p-3 flex items-start gap-3">
        <div className="w-8 h-8 rounded-lg bg-amber-500/15 flex items-center justify-center flex-shrink-0">
          <Zap className="w-4 h-4 text-amber-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="text-sm font-bold text-white">Auto-transcription au webhook Aircall</div>
            <Switch
              checked={!!usage.auto_transcribe_enabled}
              onCheckedChange={toggleAuto}
              disabled={saving}
              data-testid="auto-transcribe-toggle"
            />
          </div>
          <p className="text-[11px] text-slate-400 mt-0.5">
            Si activé, chaque appel Aircall avec enregistrement (event <code className="text-violet-300">call.ended</code>) sera transcrit automatiquement en arrière-plan. Coût ≈ <strong>{usage.whisper_price_eur_per_min} €/min</strong>.
          </p>
          {saving && <div className="text-[10px] text-slate-500 mt-1 inline-flex items-center gap-1"><Loader2 className="w-2.5 h-2.5 animate-spin" /> Enregistrement…</div>}
        </div>
      </div>
    </div>
  );
}

function Kpi({ icon, label, value, unit }) {
  return (
    <div className="bg-[#14101c] rounded-lg border border-white/5 p-2.5">
      <div className="text-[10px] text-slate-500 inline-flex items-center gap-1 uppercase tracking-wider">{icon} {label}</div>
      <div className="text-base font-bold text-white mt-1">
        {value}
        <span className="text-[10px] text-slate-500 ml-1 font-normal">{unit}</span>
      </div>
    </div>
  );
}
